import { ArrowUpRight, MapPin } from "lucide-react";
import { useI18n } from "@/lib/i18n";

/**
 * Clean, tracking-free location map for the Mels site.
 *
 * Uses the official OpenStreetMap embed (no Google Maps → no cookies, no
 * GDPR/CH-DSG complications). A "Route planen" button opens Google Maps in
 * a new tab on the visitor's device — so the website itself loads zero
 * third-party scripts, but visitors still get one-click navigation.
 */

// Approximate coordinates of Zeughausstrasse 12, 8887 Mels SG.
const LAT = 47.0479;
const LON = 9.4189;
const BBOX_DELTA = 0.004;

const OSM_EMBED = `https://www.openstreetmap.org/export/embed.html?bbox=${
  LON - BBOX_DELTA
},${LAT - BBOX_DELTA},${LON + BBOX_DELTA},${
  LAT + BBOX_DELTA
}&layer=mapnik&marker=${LAT},${LON}`;

const OSM_VIEW = `https://www.openstreetmap.org/?mlat=${LAT}&mlon=${LON}#map=17/${LAT}/${LON}`;

// Google Maps "directions" link works without loading Google on our site —
// it simply opens in the visitor's preferred maps app (Google Maps mobile,
// Apple Maps on iOS via universal links, web Maps on desktop).
const ROUTE_URL =
  "https://www.google.com/maps/dir/?api=1&destination=Fritz+Kamm+AG,+Zeughausstrasse+12,+8887+Mels,+Switzerland";

const COPY = {
  de: { title: "Anfahrt", sub: "Zeughausstrasse 12 · CH-8887 Mels SG", route: "Route planen", view_osm: "Auf OpenStreetMap öffnen", aria_map: "Karte von Fritz Kamm AG in Mels" },
  fr: { title: "Itinéraire", sub: "Zeughausstrasse 12 · CH-8887 Mels SG", route: "Calculer l'itinéraire", view_osm: "Ouvrir sur OpenStreetMap", aria_map: "Carte de Fritz Kamm SA à Mels" },
  it: { title: "Come arrivare", sub: "Zeughausstrasse 12 · CH-8887 Mels SG", route: "Calcola il percorso", view_osm: "Apri su OpenStreetMap", aria_map: "Mappa di Fritz Kamm SA a Mels" },
  en: { title: "Directions", sub: "Zeughausstrasse 12 · CH-8887 Mels SG", route: "Plan route", view_osm: "Open on OpenStreetMap", aria_map: "Map of Fritz Kamm AG in Mels" },
};

export default function LocationMap({ className = "" }) {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;

  return (
    <div
      data-testid="location-map"
      className={`border border-white/10 bg-[#101013] overflow-hidden ${className}`}
    >
      <div className="px-5 py-4 border-b border-white/10 flex items-start gap-3">
        <MapPin className="w-4 h-4 text-zinc-400 mt-0.5 flex-none" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] text-white">
            {c.title}
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{c.sub}</div>
        </div>
      </div>

      <div className="relative">
        {/* Subtle dark overlay so the OSM map blends with the dark theme */}
        <iframe
          title={c.aria_map}
          src={OSM_EMBED}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="block w-full h-[240px] md:h-[260px] border-0 grayscale-[0.4] contrast-90 brightness-90"
          style={{ filter: "grayscale(0.55) contrast(0.92) brightness(0.85)" }}
          data-testid="location-map-iframe"
        />
        {/* faint vignette to soften OSM borders into the dark UI */}
        <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/30" />
      </div>

      <div className="flex items-stretch divide-x divide-white/10 border-t border-white/10 text-[12px]">
        <a
          href={ROUTE_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="location-map-route"
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-white hover:bg-white/[0.04] transition-colors font-semibold tracking-[0.04em]"
        >
          {c.route}
          <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.75} />
        </a>
        <a
          href={OSM_VIEW}
          target="_blank"
          rel="noreferrer"
          data-testid="location-map-osm"
          className="hidden md:flex items-center justify-center gap-2 px-4 py-3 text-zinc-400 hover:text-white hover:bg-white/[0.02] transition-colors"
          title={c.view_osm}
        >
          {c.view_osm}
          <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} />
        </a>
      </div>
    </div>
  );
}
